/**
 * serverTruth — une seule règle de précédence pour ce que le tuteur pose sur l'élève et que l'app
 * apprend par plusieurs canaux : le son (Dialogue ou Concentré) et la main levée.
 *
 * Aujourd'hui le son arrive par quatre canaux qui s'écrasent dans l'ordre où ils tombent : les
 * réponses de `joinCall`, `getCall` et `setSoundMode` (`soundModeIds`), l'attribut `sound_mode` du
 * participant (`LKEvents`), et la valeur persistée de `liveMode`, rejouée au rechargement — une
 * séance d'hier peut ainsi rouvrir l'élève en Dialogue.
 *
 * La règle, ici :
 *  1. **La valeur persistée ne compte jamais.** Au montage, la valeur est inconnue : le défaut d'une
 *     séance neuve (Concentré, main baissée), jusqu'au premier signal de CETTE séance.
 *  2. **Un attribut poussé par la salle s'applique toujours** : c'est le serveur qui l'écrit au geste
 *     du tuteur. Une valeur vide (`""`, l'attribut d'une reconnexion, que le jeton ne porte pas) n'est
 *     pas une décision : elle est ignorée — l'ancien code y lisait un Concentré.
 *  3. **Une réponse du serveur s'applique si elle a été DEMANDÉE après la dernière décision connue** :
 *     le dernier attribut reçu, la dernière réponse appliquée, ou le geste de l'élève lui-même (la main
 *     levée change au toucher). Sinon elle décrit un état d'avant : une relecture de `getCall` demandée
 *     avant le toucher, arrivée pendant l'envoi, faisait retomber la main.
 *
 * Pur : l'heure arrive dans l'événement.
 */

export type Truth = {
    value: boolean
    /** Une décision de cette séance est arrivée : la valeur n'est plus le défaut. */
    known: boolean
    /** Quand le dernier attribut poussé est arrivé. */
    lastPushAt: number | null
    /**
     * L'heure de la dernière décision appliquée : un attribut (son arrivée), une réponse (sa demande),
     * un geste de l'élève. Une réponse demandée avant ne s'applique plus.
     */
    lastDecisionAt: number | null
    /** D'où vient la valeur : le défaut, une réponse, un attribut. */
    source: "default" | "reply" | "push"
}

export const unknownTruth = (value: boolean): Truth => ({
    value,
    known: false,
    lastPushAt: null,
    lastDecisionAt: null,
    source: "default",
})

/** L'attribut d'un participant (`"1"`, `"0"`) → une décision, ou `null` (vide : pas une décision). */
export function attributeDecision(raw: string | null | undefined): boolean | null {
    if (raw === "1" || raw === "true") return true
    if (raw === "0" || raw === "false") return false
    return null
}

/** Un attribut poussé par la salle. */
export function applyPush(truth: Truth, raw: string | null | undefined, at: number): Truth {
    const decision = attributeDecision(raw)
    if (decision == null) return truth
    return { value: decision, known: true, lastPushAt: at, lastDecisionAt: at, source: "push" }
}

/** La réponse demandée à `requestedAt` décrit-elle un état d'avant la dernière décision ? */
export function isStaleReply(truth: Truth, requestedAt: number): boolean {
    return truth.lastDecisionAt != null && requestedAt < truth.lastDecisionAt
}

/** Une réponse du serveur, demandée à `requestedAt`. */
export function applyReply(truth: Truth, value: boolean, requestedAt: number): Truth {
    if (isStaleReply(truth, requestedAt)) return truth
    return { ...truth, value, known: true, lastDecisionAt: requestedAt, source: "reply" }
}

/** Le geste de l'élève, à `at` : sa valeur, tout de suite ; une réponse demandée avant ne la défait pas. */
export function applyGesture(truth: Truth, value: boolean, at: number): Truth {
    return { ...truth, value, lastDecisionAt: at }
}
